import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { adPackages } from "@/mocks/products";
import { useSEO, SITE_URL } from "@/hooks/useSEO";

export default function PremiumAdsPage() {
  useSEO({
    title: "Premium Advertisement Packages | Reach Global Buyers | Crown Global",
    description: "Advertise your factory or products to millions of global buyers on Crown Global. Choose from Starter, Professional, or Enterprise ad packages. Targeted placement across the marketplace.",
    keywords: "premium advertising, factory advertisement, global buyers, marketplace ads, Crown Global advertising",
    canonical: "/premium-ads",
    ogTitle: "Premium Advertisement Packages — Crown Global",
    ogDescription: "Reach millions of global buyers with targeted ad placements across Crown Global marketplace.",
    ogImage: "https://public.readdy.ai/ai/img_res/a37dddd55243bb04134d280aa2e96858.jpg",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${SITE_URL}/premium-ads`,
          "url": `${SITE_URL}/premium-ads`,
          "name": "Premium Advertisement Packages — Crown Global",
          "description": "Advertise your factory or products to millions of global buyers on Crown Global marketplace.",
          "isPartOf": { "@id": `${SITE_URL}/#website` },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
              { "@type": "ListItem", "position": 2, "name": "Premium Ads", "item": `${SITE_URL}/premium-ads` }
            ]
          }
        },
        {
          "@type": "Service",
          "@id": `${SITE_URL}/premium-ads#service`,
          "url": `${SITE_URL}/premium-ads`,
          "name": "Crown Global Premium Advertising",
          "description": "Targeted ad placements reaching millions of global buyers across Crown Global marketplace.",
          "provider": { "@id": `${SITE_URL}/#organization` },
          "areaServed": "Worldwide",
          "serviceType": "Digital Advertising",
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Advertisement Packages",
            "itemListElement": [
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Starter Ad Package", "description": "Basic ad placement for small factories and sellers" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Professional Ad Package", "description": "Priority placement for growing businesses" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Enterprise Ad Package", "description": "Maximum visibility for large factories and brands" } }
            ]
          }
        }
      ]
    }
  });

  const [selected, setSelected] = useState<number | null>(null);
  const [formData, setFormData] = useState({ company: "", email: "", product: "", package: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (charCount > 500) return;
    const body = new URLSearchParams({ ...formData, package: adPackages[selected ?? 1].name });
    try {
      await fetch("https://readdy.ai/api/form/d7ckf245q56ra7tuf910", { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body: body.toString() });
    } catch (_e) { /* submission error */ }
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-32 pb-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-amber-500 text-sm font-semibold uppercase tracking-widest mb-2">Grow Your Business</p>
            <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-3">Premium Advertisement Packages</h1>
            <p className="text-gray-500 text-base max-w-xl mx-auto">Reach millions of global buyers with targeted ad placements across CrownGlobal's marketplace.</p>
          </div>

          {/* Packages */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
            {adPackages.map((pkg, i) => (
              <div key={pkg.id} className={`relative rounded-2xl p-8 bg-gradient-to-br ${pkg.color} border-2 transition-all duration-200 cursor-pointer ${selected === i ? "border-amber-400 scale-105" : "border-transparent hover:border-amber-200"}`} onClick={() => setSelected(i)}>
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">Most Popular</div>
                )}
                <h3 className="text-xl font-extrabold text-gray-900 mb-1">{pkg.name}</h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-extrabold text-gray-900">${pkg.price}</span>
                  <span className="text-gray-400 text-sm">/{pkg.period}</span>
                </div>
                <ul className="flex flex-col gap-2.5 mb-8">
                  {pkg.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-fill text-amber-400 mt-0.5 shrink-0"></i>
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={(e) => { e.stopPropagation(); setSelected(i); document.getElementById("ad-form")?.scrollIntoView({ behavior: "smooth" }); }}
                  className={`w-full font-bold py-3 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${selected === i ? "bg-amber-400 text-white hover:bg-amber-500" : "bg-white text-gray-800 hover:bg-amber-50 border border-gray-200"}`}
                >
                  {selected === i ? "Selected" : "Choose Plan"}
                </button>
              </div>
            ))}
          </div>

          {/* Ad form */}
          <div id="ad-form" className="bg-white rounded-2xl p-8 md:p-12 max-w-2xl mx-auto">
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Submit Your Ad Request</h2>
            <p className="text-gray-500 text-sm mb-6">Fill in your details and our team will contact you within 24 hours.</p>
            {submitted ? (
              <div className="flex flex-col items-center py-10 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mb-4">
                  <i className="ri-checkbox-circle-fill text-green-500 text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Request Submitted!</h3>
                <p className="text-gray-500 text-sm">Our advertising team will reach out within 24 hours.</p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Company Name *</label>
                    <input name="company" required value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})} placeholder="Your company name" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Email Address *</label>
                    <input name="email" type="email" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="your@email.com" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Product / Service to Advertise *</label>
                  <input name="product" required value={formData.product} onChange={e => setFormData({...formData, product: e.target.value})} placeholder="What would you like to advertise?" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Selected Package</label>
                  <select name="package" value={selected !== null ? adPackages[selected].name : ""} onChange={e => setSelected(adPackages.findIndex(p => p.name === e.target.value))} className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-amber-400 cursor-pointer bg-white">
                    <option value="">Choose a package</option>
                    {adPackages.map(p => <option key={p.id} value={p.name}>{p.name} — ${p.price}/{p.period}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Additional Notes <span className="text-gray-400 font-normal">({charCount}/500)</span></label>
                  <textarea name="message" value={formData.message} onChange={e => { if (e.target.value.length <= 500) { setFormData({...formData, message: e.target.value}); setCharCount(e.target.value.length); }}} placeholder="Any specific requirements or questions..." rows={4} className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors resize-none" />
                  {charCount >= 500 && <p className="text-red-500 text-xs mt-1">Maximum 500 characters reached.</p>}
                </div>
                <button type="submit" className="bg-amber-400 hover:bg-amber-500 text-white font-bold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">Submit Ad Request</button>
              </form>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
